/**
 * Notes-pipeline API client (Kith hackathon — composer / confirm queue /
 * notes feed).
 *
 * Thin client over the note-pipeline contract being built in parallel:
 *   POST /api/t/{slug}/v1/clients/{clientId}/notes
 *   GET  /api/t/{slug}/v1/clients/{clientId}/notes?page=N
 *   GET  /api/t/{slug}/v1/clients/{clientId}/proposals?status=proposed&status=held_for_review
 *        (the open queue; ?status=confirmed or ?status=dismissed for one state)
 *   POST /api/t/{slug}/v1/proposals/{proposalId}/resolve
 *
 * Mirrors src/lib/api.ts fetch semantics (native fetch, ApiError
 * normalization, redirect:'manual' on writes for the X.5.4 auth-expiry
 * signal) without touching that file — when the real API merges, wiring
 * is confined to THIS file.
 *
 * MOCK SEAM: `notesApi` is chosen once at module load — the dev-only
 * in-memory mock (src/lib/notesMock.ts) when its flag is on, the real
 * fetch client otherwise. The flag defaults OFF; the dev preview flips
 * it via ?mockNotes=1 or localStorage. Deleting the mock later =
 * delete notesMock.ts + collapse the one ternary below.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import { serverSentence } from './errorCopy';

// ---------------------------------------------------------------------
// Wire shapes (per the parallel-branch API contract)
// ---------------------------------------------------------------------

/**
 * Rate bands render from this config array (single source — the composer
 * takes it as a prop default rather than scattering label strings).
 * Values are the snake_case wire identifiers; labels are display copy.
 */
export interface RateBandOption {
  value: string;
  label: string;
  /** The band's client rate is a price per visit, not per hour.
   *  Present only as the JSON boolean true in tenant config. Mirrors
   *  api/_shared/rateBands.ts. */
  per_visit?: true;
  /** Which service the band belongs to; absent reads as care management
   *  (the SPICE session pair is SPICE regardless). */
  family?: 'spice' | 'care_management';
}

/** The SPICE session pair (mirrors bandVisibility.SPICE_NOTE_BANDS). */
const SPICE_SESSION_BANDS: readonly string[] = ['spice', 'spice_friendly_visitor'];

/** Whether a band is a per-visit service in this band list:
 *  decided by the attribute alone, never by the band's name. */
export function isPerVisitBand(band: string, bands: readonly RateBandOption[]): boolean {
  return bands.some((b) => b.value === band && b.per_visit === true);
}

/** The service a band belongs to: SPICE for the session pair and for
 *  `family: 'spice'`, care management for everything else. */
export function bandFamily(band: string, bands: readonly RateBandOption[]): 'spice' | 'care_management' {
  if (SPICE_SESSION_BANDS.includes(band)) return 'spice';
  return bands.find((b) => b.value === band)?.family === 'spice' ? 'spice' : 'care_management';
}

export const RATE_BANDS: readonly RateBandOption[] = [
  { value: 'care_management', label: 'Care management' },
  { value: 'travel', label: 'Travel' },
  { value: 'spice', label: 'SPICE' },
  { value: 'spice_friendly_visitor', label: 'SPICE - Friendly Visitor' },
  { value: 'admin', label: 'Admin' },
];

/**
 * Read the tenant rate-band options from tenants.config
 * (`config.features.rate_bands`: array of `{ value, label }` with
 * non-empty strings), falling back to RATE_BANDS when unset/malformed/
 * empty (COMMERCIALIZATION D2 config seam; readRiskCategories
 * defensive-walk precedent — only well-formed entries are kept, never a
 * blank picker). The server accepts rate_band as free text
 * (optionalTrimmedString in validators/encounterNotes.ts — no closed
 * set), so the vocabulary is FE-owned and this is the one read site.
 * Pure; unit-tested.
 */
export function readRateBands(config: unknown): readonly RateBandOption[] {
  if (typeof config !== 'object' || config === null) return RATE_BANDS;
  const features = (config as Record<string, unknown>).features;
  if (typeof features !== 'object' || features === null) return RATE_BANDS;
  const v = (features as Record<string, unknown>).rate_bands;
  if (!Array.isArray(v)) return RATE_BANDS;
  const bands: RateBandOption[] = [];
  for (const entry of v) {
    if (typeof entry !== 'object' || entry === null) continue;
    const value = (entry as Record<string, unknown>).value;
    const label = (entry as Record<string, unknown>).label;
    if (
      typeof value === 'string' &&
      value.trim().length > 0 &&
      typeof label === 'string' &&
      label.trim().length > 0
    ) {
      const band: RateBandOption = { value: value.trim(), label: label.trim() };
      // Only the exact values count: the string "true" is not
      // per-visit, and a family is one of its two strings or absent.
      const perVisit = (entry as Record<string, unknown>).per_visit;
      if (perVisit === true) band.per_visit = true;
      const family = (entry as Record<string, unknown>).family;
      if (family === 'spice' || family === 'care_management') band.family = family;
      bands.push(band);
    }
  }
  return bands.length > 0 ? bands : RATE_BANDS;
}

/** Feed entry kind → Chip variant mapping lives in NotesFeed. Optional on
 * the wire; absent reads as 'visit' (a CM-authored visit note).
 * 'note' | 'assessment' are the SERVER kinds (0122 encounter_notes.kind —
 * the pipeline door: 'note' = the composer path, rendered like a visit;
 * 'assessment' = the comprehensive intake assessment). The other three
 * are the original display-only concept kept for the mock seam. */
export type NoteKind = 'visit' | 'family' | 'system' | 'note' | 'assessment';

export interface NoteWire {
  id: string;
  /** The visit's calendar day, 'YYYY-MM-DD' (a DATE column; never a timestamp). */
  occurred_at: string;
  rate_band: string;
  hours: number;
  after_hours: boolean;
  /** 0248: the author ticked "Long-distance visit" on a SPICE-family note:
   *  it pays them one SPICE Travel line and never the client. The feed's
   *  "Long-distance" chip reads it. Always set from a server row
   *  (toNoteWire); optional for the mock seam and fixtures, where absent
   *  reads as unticked. */
  long_distance_travel?: boolean;
  raw_text: string;
  invoice_summary_draft: string | null;
  /** Drafter sufficiency gate (0121): why the pipeline deliberately
   * withheld the draft ('insufficient_content' = the note's content
   * does not justify a drafted line); the feed invites the CM to write
   * the line. */
  invoice_summary_skip_reason: string | null;
  status: string;
  /** Where the note came from: 'native' (written in Olive) or
   *  'caretree_import' (loaded from CareTree). A note that is not native
   *  is never offered to the reader (`isNativeNote`). */
  source: string;
  /** The author's DISPLAY NAME (resolved server-side) — never a raw uuid. */
  author: string;
  /** The author's user id — for deep links only; never rendered on screen. */
  author_user_id: string;
  posted_at: string;
  /** The last status change; the feed reads it to tell a live read from
   * one the hosting cut short. Optional for the mock seam. */
  updated_at?: string;
  kind?: NoteKind;
  /** 0125 removal facts: non-null voided_at = the note was removed
   * (rendered muted under the feed's "Show removed" toggle; excluded
   * from the default list). */
  voided_at: string | null;
  void_reason: string | null;
  /** The version link: the replacement note when the removal was an
   * edit (supersede). */
  superseded_by_note_id: string | null;
  /**
   * The invoice line was written by a person (edited-by-human wins), the
   * server's word: the note's change history has a line edit newer than
   * its newest restart. The author's own edit counts; a line filed with
   * the note does not, and a restart ends it.
   */
  summary_edited?: boolean;
  /**
   * LP-45: the pay period holding the note's date has closed, the
   * server's word. Its hours, date, rate band and After hours can't
   * change and it can't be removed; its words still can. Absent reads as
   * open.
   */
  pay_period_closed?: boolean;
  /**
   * LP-45: for the person reading, the period has closed and they are not
   * the office (a holder of view_all_clinical), so the four payroll inputs
   * and the long-distance tick can't change. The server's word; absent
   * reads as open.
   */
  pay_inputs_locked?: boolean;
  /** Who removed the note (a DISPLAY NAME), when it is removed and the
   *  name resolves; null otherwise. The quiet "Removed by" line. */
  voided_by?: string | null;
  /** The newest edit (billing facts, the invoice line, or the Edit that
   *  made this note): who (a DISPLAY NAME, null when it does not
   *  resolve) and when (ISO). null when the note was never edited;
   *  Mark as ready, extraction and removal are not edits. The quiet
   *  "Edited by" line. */
  last_edited?: { by: string | null; at: string } | null;
  /** The Edit that made this note changed its long-distance tick: the
   *  edited line says "Long-distance visit added" or "removed". null or
   *  absent when it did not. */
  long_distance_change?: 'added' | 'removed' | null;
  /** The human-confirmed Ready gate: non-null = a person marked this
   *  note ready for billing (ISO). Drives the card's ready line. */
  billing_ready_at: string | null;
}

/** The source of a note written in Olive. */
export const NATIVE_NOTE_SOURCE = 'native';

/**
 * Whether the note was written in Olive. The one predicate the feed's
 * read doors ask: a note loaded from CareTree is never offered to the
 * reader (no Unread chip, no Read this note now, no Try again).
 */
export function isNativeNote(note: Pick<NoteWire, 'source'>): boolean {
  return note.source === NATIVE_NOTE_SOURCE;
}

export interface NotesListResponse {
  notes: NoteWire[];
  page: number;
  total: number;
  /** Optional on the wire; when absent the feed infers it from page 1's length. */
  page_size?: number;
}

export interface NoteCreateInput {
  occurred_at: string;
  rate_band: string;
  // null = blank (server rejects for case notes); 0 = a valid no-charge post.
  hours: number | null;
  after_hours: boolean;
  /** 0248: the long-distance tick, sent on every post and Edit (the box
   *  offers it on a SPICE-family band only). */
  long_distance_travel: boolean;
  raw_text: string;
  /** The pipeline door (0122 encounter_notes.kind; server default
   *  'note'). 'assessment' = the paste-an-assessment door that seeds
   *  the record (#409). */
  kind?: 'note' | 'assessment';
  /** 0130: the author's server draft this post consumes — the server
   *  deletes the draft in the same transaction as the note insert. */
  draft_id?: string;
  /** The author's word that a second SPICE visit for the same client,
   *  day and band is real: true files past the server's same-day guard
   *  (its duplicate_spice_visit refusal). Omitted on an ordinary post. */
  second_visit_confirmed?: boolean;
  /** The invoice line the note is filed with (a create only; a SPICE
   *  visit files with its line in one request). A create is never an
   *  edit, so the note does not read as edited for it. Omitted when the
   *  note has no line yet. */
  invoice_summary_draft?: string;
}

/**
 * Proposal shape: one of the 14 proposal_type values pinned in the payload
 * registry (api/_shared/noteVocabulary.ts, mirrored FE-side in
 * @/lib/noteVocabulary). The ConfirmQueue reads each type's typed fields
 * from that registry; a type without a typed card yet reads its summary
 * line, and an unknown type still renders (generic summary) rather than
 * crashing — hence proposal_type stays a loose string on the wire.
 */
/** One active condition already on the client's record (dedupe hint). */
export interface ConditionMatchRow {
  id: string;
  name: string;
  status: 'active' | 'watch' | 'resolved';
}

/** Conditions dedupe classification for a condition proposal (confirm
 *  queue). `exact` = the row the server auto-joins at confirm (the card
 *  shows it); `near` = similar rows the CM decides about. */
export interface ConditionMatch {
  exact: ConditionMatchRow | null;
  near: ConditionMatchRow[];
}

/** One active medication already on the client's record (change-matching
 *  hint — dose/schedule ride along so the card can say "Xanax (10 mg)"). */
export interface MedicationMatchRow {
  id: string;
  name: string;
  dose: string | null;
  schedule: string | null;
  status: 'active' | 'discontinued';
}

/** Medication change-matching classification for a medication proposal
 *  (confirm queue — founder ruling 2026-07-25). `exact` = an active row
 *  with the SAME normalized name; `near` = "did you mean" rows within a
 *  small edit distance. BOTH tiers are explicit CM choices (Update
 *  instead / Keep both) — the server never auto-joins on name alone, and
 *  the near tier is NEVER auto-selected (look-alike/sound-alike drug
 *  pairs are real distinct drugs one to two letters apart). */
export interface MedicationMatch {
  exact: MedicationMatchRow | null;
  near: MedicationMatchRow[];
}

export interface ProposalWire {
  id: string;
  /** The parent note (0125: lets the remove dialog count a note's
   * confirmed updates from the client's proposal list). */
  note_id?: string;
  proposal_type: string;
  payload: Record<string, unknown>;
  source_quote: string;
  confidence: number;
  /** held_for_review: a record update Olive could not read cleanly. */
  status: 'proposed' | 'held_for_review' | 'confirmed' | 'dismissed';
  /** Conditions dedupe hint (condition proposals only). */
  condition_match?: ConditionMatch;
  /** Medication change-matching hint (medication proposals only). */
  medication_match?: MedicationMatch;
}

export interface ProposalsResponse {
  proposals: ProposalWire[];
}

/** Which proposals a list read asks for: 'open' is both unresolved
 *  states (proposed and held_for_review), in one request. */
export type ProposalListFilter = 'open' | 'confirmed' | 'dismissed';

/** The unresolved states, in the order the open read asks for them. */
export const OPEN_PROPOSAL_STATUSES = ['proposed', 'held_for_review'] as const;

export type ProposalAction = 'confirmed' | 'dismissed';

export interface ProposalResolveInput {
  action: ProposalAction;
  /**
   * The CM's confirm-time field edits. GC-ratified at assembly
   * (2026-07-05): the server merges these into the confirmed record
   * event's payload — edits win; dismissals ignore payload.
   */
  payload?: Record<string, unknown>;
  /**
   * Update an existing row instead of inserting (the [B2] confirm
   * contract). Sent when the CM chooses to merge a NEAR-match condition
   * into an existing one (EXACT condition matches auto-join server-side,
   * so the card need not send it for those), or when the CM taps
   * "Update instead" on a medication change-matching hint — medication
   * exact-NAME matches are NOT write-time auto-joins (the silent dedupe
   * stays strict name+dose+schedule), so the card sends the target for
   * BOTH medication tiers. Ignored on dismissal.
   */
  target_row_id?: string | null;
}

/** 0125 remove-note response facts (no content echo). */
export interface RemoveNoteResult {
  note_id: string;
  dismissed_proposal_count: number;
  /** Confirmed record updates that STAY on the record. */
  confirmed_update_count: number;
}

/** 0125 supersede (edit) response: the replacement note. */
export interface SupersedeNoteResult {
  note: NoteWire;
  superseded_note_id: string;
}

export interface NotesApi {
  createNote(
    slug: string,
    clientId: string,
    input: NoteCreateInput,
    signal?: AbortSignal,
  ): Promise<NoteWire>;
  listNotes(
    slug: string,
    clientId: string,
    page: number,
    /** q: the notes search's words (PR 6b). Non-blank searches with POST
     *  /notes/search, the words in the body, never in a URL. */
    opts?: { includeRemoved?: boolean; q?: string },
    signal?: AbortSignal,
  ): Promise<NotesListResponse>;
  listProposals(
    slug: string,
    clientId: string,
    filter?: ProposalListFilter,
    signal?: AbortSignal,
  ): Promise<ProposalsResponse>;
  resolveProposal(
    slug: string,
    proposalId: string,
    input: ProposalResolveInput,
    signal?: AbortSignal,
  ): Promise<ProposalWire>;
  /** PATCH the pipeline-drafted invoice line (edited-by-human wins). */
  updateNoteSummary(
    slug: string,
    noteId: string,
    invoiceSummaryDraft: string,
    signal?: AbortSignal,
  ): Promise<NoteWire>;
  /** Mark/unmark a note human-confirmed Ready for billing (0133). The
   * server validates eligibility on mark and emits a change_log trail.
   * Returns the facts-only response; the caller refetches the note and
   * reads `billable_event` from the body (markReadyOutcomeOf in
   * lib/prebilling) to say whether an invoice line is coming. */
  setBillingReady(
    slug: string,
    noteId: string,
    ready: boolean,
    signal?: AbortSignal,
  ): Promise<unknown>;
  /** Remove a note (0125 void). The note's history stays server-side;
   * the feed just stops showing it by default. Optional short reason. */
  removeNote(
    slug: string,
    noteId: string,
    reason?: string,
    signal?: AbortSignal,
  ): Promise<RemoveNoteResult>;
  /** Edit a note (0125 supersede): posts the full replacement body; the
   * server creates the new note and retires the old one with a version
   * link, in one transaction. The caller then fires extraction on the
   * NEW note (same client-driven invoker as a fresh post). */
  supersedeNote(
    slug: string,
    noteId: string,
    input: NoteCreateInput,
    signal?: AbortSignal,
  ): Promise<SupersedeNoteResult>;
  /** The Era-A extraction invoker (POST .../notes/{noteId}/extract):
   * synchronous — resolves when this CALL's work has finished. For a
   * case note that is the whole extraction; for an assessment the
   * server processes ONE chunk per call and answers status 'partial'
   * with progress until the last chunk — use runExtractionToCompletion
   * to drive the loop. The composer awaits it after every post; the
   * feed re-fires it for retry and stuck-note recovery (which RESUMES a
   * half-done assessment). Safe to double-fire (server-side in-progress
   * refusal + dedup keys). */
  runExtraction(
    slug: string,
    noteId: string,
    signal?: AbortSignal,
    opts?: { fresh?: boolean },
  ): Promise<ExtractionRunStatus>;
}

/** The extract route's wire answer. completed/total arrive on
 * assessment calls only (the chunked loop). */
export interface ExtractionRunStatus {
  note_id: string;
  status: string;
  completed_chunks?: number;
  total_chunks?: number;
}

/**
 * The delays between retry attempts, per section. Retrying is free by
 * design: the server resumes at extraction_cursor and double-fires are
 * safe (FOR UPDATE serialization plus note-scoped idempotency keys), so
 * the client absorbs transient failures instead of surfacing them:
 *   - a network kill ("failed to fetch": the hosting's HTTP window
 *     killing a slow section) resumes where it died;
 *   - a 5xx (a 502 extraction_failed among them; the server already
 *     flipped the status) re-runs and resumes at the failed section;
 *   - a 409 extraction_in_progress is not a retry but a wait (see
 *     EXTRACTION_BUSY_POLL_MS); once a holder has shown no progress for
 *     the whole busy window it counts like any other failure, and the
 *     last delay here still outlasts STALE_EXTRACTING_MS so a read the
 *     window cut short runs again on its own.
 * 4xx auth/validation failures and operator aborts are never retried.
 * Exported for tests (fake-timer advancement).
 */
export const EXTRACTION_RETRY_DELAYS_MS = [2000, 6000, 12000, 60000] as const;

/**
 * The server treats an 'extracting' flip older than this as a killed
 * request and lets the read run again. The last retry delay above
 * outlasts it on purpose: after the hosting's HTTP wall cuts a read, the
 * earlier re-fires meet the live-read refusal (409) and the final one
 * arrives once the flip has aged past the window, so the read finishes
 * without anyone clicking. Mirrors the API's STALE_EXTRACTING_MS.
 */
export const STALE_EXTRACTING_MS = 60_000;

/**
 * The route's refusal of a note not written in Olive: a 409 whose body
 * says note_migrated. It is final, so the loop never retries it.
 */
function isMigratedNoteRefusal(err: ApiError): boolean {
  const body = err.body;
  return (
    err.status === 409 && typeof body === 'object' && body !== null && (body as { error?: unknown }).error === 'note_migrated'
  );
}

function isRetryableExtractionFailure(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (isMigratedNoteRefusal(err)) return false;
    return err.status === 409 || err.status >= 500;
  }
  // fetch's network failure ("TypeError: Failed to fetch") — THE reported
  // failure mode. An AbortError is a DOMException, not a TypeError, so an
  // operator abort never lands here.
  return err instanceof TypeError;
}

/** How often a waiter asks again while another request holds the read. */
export const EXTRACTION_BUSY_POLL_MS = 5000;
/**
 * How many times a waiter asks while the holder shows no progress at one
 * cursor value: one poll past the server's stale window, so a holder the
 * host killed ages out and the read runs again. A holder that advances
 * the cursor resets this; a waiter never re-reads a note that finished.
 */
export const EXTRACTION_BUSY_MAX_WAITS = 13;
/** Consecutive answers that report no new section before the loop stops. */
export const EXTRACTION_NO_PROGRESS_STRIKES = 3;

/**
 * The route's busy-lock answer, and only that: a 409 whose body says
 * extraction_in_progress. Every other 409 (a removed note among them) is
 * a failure, counted like any other.
 */
export function isBusyLock(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body;
  return typeof body === 'object' && body !== null && (body as { error?: unknown }).error === 'extraction_in_progress';
}

function busyLockCursor(err: ApiError): number | null {
  const body = err.body as { extraction_cursor?: unknown } | null;
  return typeof body?.extraction_cursor === 'number' ? body.extraction_cursor : null;
}

/** A sleep the operator can abort: rejects with an AbortError at once. */
function sleepUnlessAborted(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException('The read was aborted.', 'AbortError'));
    };
    if (signal?.aborted) {
      abort();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

/**
 * Drive a read to completion: call the extract route until the server
 * stops answering 'partial'. A short case note completes in one call; an
 * assessment, or a case note long enough to read in sections, takes one
 * call per section and one more for the billing line, with onProgress
 * fired after each section ("Reading the assessment… 2 of 5").
 *
 * Failures are retried in place with backoff, and the budget of
 * EXTRACTION_RETRY_DELAYS_MS.length retries is per section: it refills
 * after every call that succeeds, so a long note with a few slow
 * sections still finishes. The route's busy answer (another request holds
 * the read) is a wait, not a retry, bounded per cursor value by
 * EXTRACTION_BUSY_MAX_WAITS. The loop is bounded by progress: each
 * 'partial' must report a section the last one did not, and once the
 * section count is known a ceiling of twice that plus twenty successful
 * calls ends a read that never finishes. Retries and busy waits sit
 * outside that bound. `opts.fresh` marks the first call as the explicit
 * restart of a finished read (the note's author or a moderating seat;
 * it takes the note off Ready and is refused on an invoice). No door in
 * the app sends it: the feed's recovery doors only ever finish an
 * unfinished read. The loop's later calls never carry it, so a waiter
 * that finds the note finished stops there.
 */
export async function runExtractionToCompletion(
  slug: string,
  noteId: string,
  onProgress?: (done: number, total: number) => void,
  signal?: AbortSignal,
  opts: { fresh?: boolean } = {},
): Promise<ExtractionRunStatus> {
  let retriesUsed = 0;
  let firstCall = true;
  let busyWaits = 0;
  let busyCursorSeen: number | null = null;
  let strikes = 0;
  let lastCompleted: number | null = null;
  let successfulCalls = 0;
  let ceiling: number | null = null;
  for (;;) {
    let last: ExtractionRunStatus;
    try {
      last = await notesApi.runExtraction(slug, noteId, signal, firstCall && opts.fresh ? { fresh: true } : undefined);
      firstCall = false;
    } catch (err) {
      if (signal?.aborted) throw err;
      if (err instanceof ApiError && isBusyLock(err)) {
        const cursor = busyLockCursor(err);
        if (cursor !== null && (busyCursorSeen === null || cursor > busyCursorSeen)) {
          busyCursorSeen = cursor;
          busyWaits = 0;
        }
        if (busyWaits < EXTRACTION_BUSY_MAX_WAITS) {
          busyWaits += 1;
          await sleepUnlessAborted(EXTRACTION_BUSY_POLL_MS, signal);
          continue;
        }
        // The holder has shown no progress for the whole window: from here
        // the busy answer counts like any other failure.
      }
      if (retriesUsed >= EXTRACTION_RETRY_DELAYS_MS.length || !isRetryableExtractionFailure(err)) {
        throw err;
      }
      const delay = EXTRACTION_RETRY_DELAYS_MS[retriesUsed]!;
      retriesUsed += 1;
      await sleepUnlessAborted(delay, signal);
      continue;
    }
    retriesUsed = 0;
    successfulCalls += 1;
    if (last.status !== 'partial') return last;
    const completed = last.completed_chunks ?? 0;
    const total = last.total_chunks ?? 0;
    onProgress?.(completed, total);
    if (lastCompleted !== null && completed <= lastCompleted) {
      strikes += 1;
      if (strikes >= EXTRACTION_NO_PROGRESS_STRIKES) {
        throw new Error(`extraction made no progress (note ${noteId})`);
      }
    } else {
      strikes = 0;
    }
    lastCompleted = completed;
    if (ceiling === null && total > 0) ceiling = 2 * total + 20;
    if (ceiling !== null && successfulCalls >= ceiling) {
      throw new Error(`extraction did not finish (note ${noteId})`);
    }
  }
}

/**
 * The server's on-screen refusal copy, only for a code whose `message` is
 * written for the screen (SERVER_SENTENCE_CODES.notes); null otherwise,
 * so a validator's detail never reaches a note surface.
 */
export function serverMessageFrom(err: unknown): string | null {
  return serverSentence(err, 'notes');
}

/**
 * The server's error code (the `error` field of a refusal's body), for a
 * surface that answers one refusal differently from the rest; null when
 * the failure carried none.
 */
export function serverErrorCodeFrom(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const code = (err.body as { error?: unknown }).error;
    if (typeof code === 'string' && code.length > 0) return code;
  }
  return null;
}

/** The server's sentence for a refused note date, or null when the failure
 *  is anything else. A surface shows it under its date box. */
export function noteDateRefusal(err: unknown): string | null {
  const code = serverErrorCodeFrom(err);
  return code === 'note_date_in_future' || code === 'note_date_pay_period_closed' ? serverMessageFrom(err) : null;
}

/** The line under a new note's date when its pay period has closed and the
 *  person posting is not the office (LP-45); the server refuses the post
 *  with the same words (note_date_pay_period_closed). */
export const PAY_PERIOD_CLOSED_POST_LINE =
  "This date's pay period has closed, so a note can't be added for it. Ask the office to add it.";

/** The line every door that edits a posted note shows when the person
 *  editing did not write it: an edit keeps the note, and its hours, with
 *  its author. */
export const NOT_THE_AUTHOR_EDIT_LINE =
  'Another team member wrote this note, so its hours count as theirs. To log your own time, write a new case note.';

/** The line both edit faces show, once, above the four locked controls
 *  when the note's pay period has closed (LP-45). */
export const PAY_PERIOD_CLOSED_EDIT_LINE =
  "This note's pay period has closed, so its hours, date and rate can't change. Ask the office if something is wrong.";

/** Whether the person editing is not the note's author. Decided on user
 *  ids alone, never a display name (two logins can share one), and false
 *  while the viewer is not known yet. */
export function editorIsNotAuthor(viewerUserId: string | null | undefined, authorUserId: string): boolean {
  return viewerUserId != null && viewerUserId !== authorUserId;
}

// ---------------------------------------------------------------------
// Real fetch client (mirrors api.ts get/post semantics)
// ---------------------------------------------------------------------

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return await res.text().catch(() => null);
  }
}

async function get<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(tenantUrl(slug, path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `GET ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function send<T>(
  method: 'POST' | 'PATCH',
  slug: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await parseErrorBody(res),
      `${method} ${path} → HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

function post<T>(
  slug: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  return send<T>('POST', slug, path, body, signal);
}

// Server row shapes (assembly 2026-07-05: the real API's wire — repos
// return pg rows; numerics travel as strings; lists ride under `rows`).
interface ServerNoteRow {
  id: string;
  occurred_at: string;
  rate_band: string | null;
  hours: string | null;
  after_hours: boolean;
  /** 0248; optional for the mock seam + older cached shapes (absent reads
   *  as unticked). */
  long_distance_travel?: boolean;
  raw_text: string;
  invoice_summary_draft: string | null;
  invoice_summary_skip_reason?: string | null;
  status: string;
  author_user_id: string;
  /** Author display name resolved in the read path (list route); absent on
   *  the create/update single-note shapes + the mock seam. */
  author_display_name?: string | null;
  updated_by_user_id: string;
  posted_at: string;
  /** The last status change; optional for the mock seam + older shapes. */
  updated_at?: string;
  /** 0125 removal facts; optional for the mock seam + older shapes. */
  voided_at?: string | null;
  void_reason?: string | null;
  superseded_by_note_id?: string | null;
  /** Sent by the API since 0122 ('note' | 'assessment' — the pipeline
   * door); optional for the mock seam + older cached shapes (absent
   * reads as 'visit'). */
  kind?: NoteKind;
  /** Where the note came from ('native' | 'caretree_import'); optional
   *  for older cached shapes (absent reads as 'native'). */
  source?: string;
  /** 0133 human-confirmed Ready gate (timestamptz -> ISO on the wire);
   *  optional for the mock seam + older cached shapes. */
  billing_ready_at?: string | null;
  /** The quiet-line facts (list route only; absent on the single-note
   *  shapes and older cached shapes, which read as never edited). */
  voided_by_display_name?: string | null;
  last_edited_at?: string | null;
  last_edited_by_display_name?: string | null;
  summary_edited?: boolean;
  /** LP-45: the pay period holding the note's date has closed (list
   *  route only; absent reads as open). */
  pay_period_closed?: boolean;
  /** LP-45: for the person reading, closed and not the office. */
  pay_inputs_locked?: boolean;
  long_distance_change?: 'added' | 'removed' | null;
}

interface ServerProposalRow {
  id: string;
  note_id?: string;
  proposal_type: string;
  payload: Record<string, unknown>;
  source_quote: string | null;
  confidence: string;
  status: 'proposed' | 'held_for_review' | 'confirmed' | 'dismissed';
  condition_match?: ConditionMatch;
  medication_match?: MedicationMatch;
}

function toNoteWire(row: ServerNoteRow): NoteWire {
  return {
    id: row.id,
    occurred_at: row.occurred_at,
    rate_band: row.rate_band ?? '',
    hours: row.hours === null ? 0 : Number(row.hours),
    after_hours: row.after_hours,
    long_distance_travel: row.long_distance_travel === true,
    raw_text: row.raw_text,
    invoice_summary_draft: row.invoice_summary_draft,
    invoice_summary_skip_reason: row.invoice_summary_skip_reason ?? null,
    status: row.status,
    // A server answer or a cached shape without the field reads native
    // and keeps today's doors.
    source: row.source ?? NATIVE_NOTE_SOURCE,
    // The read path resolves the display name; the raw uuid never renders.
    author: row.author_display_name ?? 'Unknown',
    author_user_id: row.author_user_id,
    posted_at: row.posted_at,
    updated_at: row.updated_at ?? row.posted_at,
    voided_at: row.voided_at ?? null,
    void_reason: row.void_reason ?? null,
    superseded_by_note_id: row.superseded_by_note_id ?? null,
    billing_ready_at: row.billing_ready_at ?? null,
    ...(row.kind !== undefined ? { kind: row.kind } : {}),
    // The server's word, from the note's change history. Never inferred
    // from updated_by: a colleague's recovery read or a moderating seat's
    // Edit leaves updated_by on someone other than the author without
    // anyone writing the line.
    summary_edited: row.summary_edited === true,
    // The server's word (notePayPeriod); the browser never works a pay
    // period out for itself.
    pay_period_closed: row.pay_period_closed === true,
    pay_inputs_locked: row.pay_inputs_locked === true,
    voided_by: row.voided_by_display_name ?? null,
    last_edited:
      typeof row.last_edited_at === 'string' && row.last_edited_at.length > 0
        ? { by: row.last_edited_by_display_name ?? null, at: row.last_edited_at }
        : null,
    long_distance_change: row.long_distance_change ?? null,
  };
}

function toProposalWire(row: ServerProposalRow): ProposalWire {
  return {
    id: row.id,
    ...(row.note_id !== undefined ? { note_id: row.note_id } : {}),
    proposal_type: row.proposal_type,
    payload: row.payload,
    source_quote: row.source_quote ?? '',
    confidence: Number(row.confidence),
    status: row.status,
    ...(row.condition_match !== undefined ? { condition_match: row.condition_match } : {}),
    ...(row.medication_match !== undefined ? { medication_match: row.medication_match } : {}),
  };
}

const realNotesApi: NotesApi = {
  async createNote(slug, clientId, input, signal) {
    const res = await post<{ note: ServerNoteRow }>(
      slug,
      `/clients/${encodeURIComponent(clientId)}/notes`,
      input as unknown as Record<string, unknown>,
      signal,
    );
    return toNoteWire(res.note);
  },

  async listNotes(slug, clientId, page, opts, signal) {
    const q = opts?.q?.trim() ?? '';
    if (q !== '') {
      const found = await post<{ rows: ServerNoteRow[]; total: number; page: number; page_size: number }>(
        slug,
        `/clients/${encodeURIComponent(clientId)}/notes/search`,
        { q, page, include_voided: opts?.includeRemoved === true },
        signal,
      );
      return { notes: found.rows.map(toNoteWire), page: found.page, total: found.total, page_size: found.page_size };
    }
    const removedParam = opts?.includeRemoved ? '&include_voided=1' : '';
    const res = await get<{ rows: ServerNoteRow[]; total: number; page: number; page_size: number }>(
      slug,
      `/clients/${encodeURIComponent(clientId)}/notes?page=${encodeURIComponent(String(page))}${removedParam}`,
      signal,
    );
    return {
      notes: res.rows.map(toNoteWire),
      page: res.page,
      total: res.total,
      page_size: res.page_size,
    };
  },

  async listProposals(slug, clientId, filter = 'open', signal) {
    const statuses: readonly string[] = filter === 'open' ? OPEN_PROPOSAL_STATUSES : [filter];
    const query = statuses.map((s) => `status=${encodeURIComponent(s)}`).join('&');
    const res = await get<{ rows: ServerProposalRow[] }>(
      slug,
      `/clients/${encodeURIComponent(clientId)}/proposals?${query}`,
      signal,
    );
    return { proposals: res.rows.map(toProposalWire) };
  },

  async resolveProposal(slug, proposalId, input, signal) {
    const res = await post<{ proposal: ServerProposalRow }>(
      slug,
      `/proposals/${encodeURIComponent(proposalId)}/resolve`,
      input as unknown as Record<string, unknown>,
      signal,
    );
    return toProposalWire(res.proposal);
  },

  async updateNoteSummary(slug, noteId, invoiceSummaryDraft, signal) {
    const res = await send<{ note: ServerNoteRow }>(
      'PATCH',
      slug,
      `/notes/${encodeURIComponent(noteId)}/summary`,
      { invoice_summary_draft: invoiceSummaryDraft },
      signal,
    );
    return toNoteWire(res.note);
  },

  async setBillingReady(slug, noteId, ready, signal) {
    // 0133 human-confirmed Ready gate. Facts-only response: the note is
    // refetched; the body's billable_event is what the feed renders from.
    return post<unknown>(
      slug,
      `/notes/${encodeURIComponent(noteId)}/billing-ready`,
      { ready },
      signal,
    );
  },

  async removeNote(slug, noteId, reason, signal) {
    return post<RemoveNoteResult>(
      slug,
      `/notes/${encodeURIComponent(noteId)}/void`,
      reason !== undefined && reason.trim() !== '' ? { reason: reason.trim() } : {},
      signal,
    );
  },

  async supersedeNote(slug, noteId, input, signal) {
    const res = await post<{ note: ServerNoteRow; superseded_note_id: string }>(
      slug,
      `/notes/${encodeURIComponent(noteId)}/supersede`,
      input as unknown as Record<string, unknown>,
      signal,
    );
    return { note: toNoteWire(res.note), superseded_note_id: res.superseded_note_id };
  },

  async runExtraction(slug, noteId, signal, opts) {
    // The production Era-A invoker: the composer's post-then-extract
    // await and the feed's retry / read-now affordances all fire this
    // one route. Extraction runs inside this request (client-side await
    // keeps the server invocation alive on SWA). fresh=1 is the explicit
    // restart of a finished read (author or moderating seat only); no
    // door sends it and the loop's own calls never do.
    return post<{ note_id: string; status: string }>(
      slug,
      `/notes/${encodeURIComponent(noteId)}/extract${opts?.fresh ? '?fresh=1' : ''}`,
      {},
      signal,
    );
  },
};

// ---------------------------------------------------------------------
// The mock seam — one ternary; delete with notesMock.ts when the real
// API merges.
// ---------------------------------------------------------------------

import { createMockNotesApi, notesMockEnabled } from './notesMock';

export const notesApi: NotesApi = notesMockEnabled() ? createMockNotesApi() : realNotesApi;

/**
 * The one activity band with no after-hours variant: there is no such
 * thing as after-hours admin work. The note composer must never offer
 * the after-hours flag when this band is selected.
 */
export const ADMIN_RATE_BAND = 'admin';

/**
 * The platform's travel band. An after-hours call-out is one care
 * management note with its travel included in the hours, so the composer
 * says so on this band whenever After hours is chosen, or the date is
 * after hours.
 */
export const TRAVEL_RATE_BAND = 'travel';

/**
 * The LEGACY band-independent after-hours price token. It priced every
 * after-hours hour, whatever the activity, at one number. It is no
 * longer priceable and no longer offered anywhere (readPriceableRateBands
 * below never returns it) — every activity band now prices its own
 * after-hours hours on its own derived band (afterHoursBandFor). The
 * token remains a legal, readable value on rows history already wrote.
 */
export const AFTER_HOURS_RATE_BAND = 'after_hours';

const AFTER_HOURS_SUFFIX = '_after_hours';

/**
 * The derived after-hours PRICE band for an activity band:
 * `<band>_after_hours`, or null for ADMIN_RATE_BAND (there is no
 * after-hours admin) and for a per-visit band (a visit price has
 * no after-hours variant). Never an ACTIVITY band: a care manager logs care
 * management, or travel, and ticks the after-hours box — after-hours is
 * not something you do, it is when you did it — so this token never
 * appears in the note composer's picker and `readRateBands` never
 * returns it.
 */
export function afterHoursBandFor(band: string, bands: readonly RateBandOption[]): string | null {
  if (band === ADMIN_RATE_BAND || isPerVisitBand(band, bands)) return null;
  return `${band}${AFTER_HOURS_SUFFIX}`;
}

/** True for any derived after-hours price-band token (`<band>_after_hours`). */
export function isAfterHoursBand(token: string): boolean {
  return token.length > AFTER_HOURS_SUFFIX.length && token.endsWith(AFTER_HOURS_SUFFIX);
}

/**
 * The bands a CLIENT RATE may be set against: every activity band the
 * tenant configured, each immediately followed by its derived
 * after-hours band (skipped for admin, which has none). The legacy
 * band-independent `after_hours` token — and any derived token a tenant
 * may already have typed into its own activity list by hand — is never
 * duplicated in here.
 */
export function readPriceableRateBands(config: unknown): readonly RateBandOption[] {
  const activity = readRateBands(config).filter(
    (b) => b.value !== AFTER_HOURS_RATE_BAND && !isAfterHoursBand(b.value),
  );
  const out: RateBandOption[] = [];
  for (const band of activity) {
    out.push(band);
    const derived = afterHoursBandFor(band.value, activity);
    if (derived !== null) {
      out.push({ value: derived, label: `${band.label} After Hours` });
    }
  }
  return out;
}

export interface PricedBandPair {
  /** The activity band (what was done). */
  activity: RateBandOption;
  /** Its derived after-hours price band, or null for admin. */
  afterHours: RateBandOption | null;
}

/**
 * The tenant's activity bands, each paired with its derived after-hours
 * band — the Rates card's one-row-per-activity-band, two-columns-of-
 * money layout. Independent of readPriceableRateBands' flat, interleaved
 * order: this walks the ACTIVITY vocabulary directly, so a pair is never
 * mis-assembled from adjacent flat entries.
 */
export function pairPriceableBands(config: unknown): readonly PricedBandPair[] {
  const bands = readRateBands(config);
  return bands
    .filter((b) => b.value !== AFTER_HOURS_RATE_BAND && !isAfterHoursBand(b.value))
    .map((activity) => {
      const derived = afterHoursBandFor(activity.value, bands);
      return {
        activity,
        afterHours: derived === null ? null : { value: derived, label: `${activity.label} After Hours` },
      };
    });
}
